import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { defineMessages, useIntl } from 'react-intl'
import { Alert, Platform, View, ViewStyle } from 'react-native'

import { useActionSheet } from '@expo/react-native-action-sheet'
import styled, { useTheme } from 'styled-components/native'

import { FileValidationErrorCode, FileValidationResult } from '@lyrahealth-inc/shared-app-logic'

import { BaseInput, BaseInputProps } from './BaseInput'
import { useWebDropzone } from './useWebDropzone'
import { IS_WEB } from '../../constants'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils'
import { type FileWithUri, pickDocumentFromLibrary, pickImageFromLibrary } from '../../utils/expoImagePicker'
import { BodyText } from '../bodyText/BodyText'
import { LoadingSpinner } from '../icons/LoadingSpinner'
import { SideNavLibraryIcon } from '../icons/SideNavLibraryIcon'
import { UploadIcon } from '../icons/UploadIcon'
import { XIcon } from '../icons/XIcon'
import { Image } from '../image/Image'
import { PressableOpacity } from '../pressableOpacity/PressableOpacity'

// Shared container height for upload and preview states
const DEFAULT_CONTAINER_HEIGHT = 100

const MAX_PREVIEW_FILE_SIZE = 5 * 1024 * 1024 // 5MB in bytes

const FILE_UPLOAD_MESSAGES = defineMessages({
  fileTooLarge: {
    defaultMessage: 'File is too large',
    description: 'File too large error message',
  },
  unsupportedFileType: {
    defaultMessage: 'Invalid file type',
    description: 'Invalid file type error message',
  },
  uploadFailed: {
    defaultMessage: 'Upload failed',
    description: 'Upload failed error message',
  },
  genericError: {
    defaultMessage: 'File validation failed',
    description: 'Generic file validation error message',
  },
  tooManyFiles: {
    defaultMessage: 'Too many files selected',
    description: 'Error message when too many files are selected',
  },
  imageSelectionFailed: {
    defaultMessage: 'Failed to select image',
    description: 'Error message when image selection fails',
  },
  fileSelectionFailed: {
    defaultMessage: 'Failed to select file',
    description: 'Error message when file selection fails',
  },
  chooseFromFiles: {
    defaultMessage: 'Choose from Files',
    description: 'Action sheet option to select a file from device storage',
  },
  chooseFromPhotoLibrary: {
    defaultMessage: 'Choose from Photo Library',
    description: 'Action sheet option to select an image from photo library',
  },
  cancel: {
    defaultMessage: 'Cancel',
    description: 'Action sheet cancel button',
  },
  uploadDocument: {
    defaultMessage: 'Upload Document',
    description: 'Title for file upload options dialog on Android',
  },
  chooseFileSource: {
    defaultMessage: 'Choose a file source',
    description: 'Message for file upload options dialog on Android',
  },
})

// ErrorCode mapping from react-dropzone
enum DropzoneErrorCode {
  FileTooLarge = 'file-too-large',
  TooManyFiles = 'too-many-files',
  FileInvalidType = 'file-invalid-type',
}

const FileUploadContainer = styled(View)<{
  theme: ThemeType
  hovered: boolean
  uploading: boolean
  height: number
  dragging?: boolean
}>(({ theme, hovered, uploading, height, dragging = false }) => ({
  borderWidth: 1,
  borderStyle: uploading ? 'solid' : 'dashed',
  borderColor: hovered || uploading || dragging ? theme.colors.borderActive : theme.colors.inputOutlineDefault,
  borderRadius: 8,
  padding: '24px 16px',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  height,
  backgroundColor:
    hovered || uploading || dragging ? theme.colors.backgroundHighlightPrimary : theme.colors.backgroundPrimary,
}))

const UploadContent = styled(View)({
  justifyContent: 'center',
  flexDirection: 'row',
  gap: 12,
  alignItems: 'center',
})

const FilePreviewWrapper = styled(View)<{ theme: ThemeType }>(({ theme }) => ({
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.colors.borderDefault,
  borderRadius: 8,
  width: '100%',
  backgroundColor: theme.colors.backgroundPrimary,
  overflow: 'hidden',
}))

const FilePreviewContainer = styled(View)<{ height: number }>(({ height }) => ({
  padding: 8,
  alignItems: 'center',
  justifyContent: 'center',
  height,
  width: '100%',
  position: 'relative',
}))

const FileSelectedContainer = styled(View)<{ theme: ThemeType; height: number }>(({ theme, height }) => ({
  flexDirection: 'row',
  justifyContent: 'space-around',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.colors.borderDefault,
  borderRadius: 8,
  padding: 8,
  alignItems: 'center',
  height,
  width: '100%',
  backgroundColor: theme.colors.backgroundPrimary,
  position: 'relative',
}))

const CLEAR_BUTTON_SIZE = 28

const ClearButton = styled(PressableOpacity)<{ theme: ThemeType; preview: boolean }>(({ theme, preview }) => ({
  width: CLEAR_BUTTON_SIZE,
  height: CLEAR_BUTTON_SIZE,
  borderRadius: CLEAR_BUTTON_SIZE / 2,
  border: `2px solid ${theme.colors.iconDefault}`,
  backgroundColor: theme.colors.backgroundSecondary,
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  position: 'absolute',
  top: preview ? 8 : '50%',
  right: 8,
  // Use marginTop instead of transform for cross-platform compatibility
  // When not in preview mode, offset by half the button height to center vertically
  ...(!preview && { marginTop: -(CLEAR_BUTTON_SIZE / 2) }),
}))

const PreviewImageContainer = styled(View)({
  width: '100%',
  height: '100%',
  borderRadius: 4,
  overflow: 'hidden',
  alignItems: 'center',
  justifyContent: 'center',
})

const FileNameBar = styled(View)<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundSecondary,
  borderTopWidth: 1,
  borderTopColor: theme.colors.borderDefault,
  padding: 16,
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
}))

const DropZoneContainer = styled(View)({
  width: '100%',
})

export interface FileUploadProps extends Omit<BaseInputProps, 'children'> {
  placeholder?: string
  subText?: string
  disabled?: boolean
  baseInputStyle?: ViewStyle
  name?: string
  isUploading?: boolean
  onFileSelect?: (file: File | null) => void
  acceptedFileTypes?: string[]
  maxFileSize?: number
  selectedFile?: File | null
  onUpload?: (file: File) => Promise<void>
  onUploadSuccessful?: (uploadedFile: File) => void
  showPreview?: boolean
  allowClear?: boolean
  errorOnSubmitFailed?: boolean
  containerHeight?: number
  onError?: () => void
  validateFile?: (file: File) => FileValidationResult
  showFileNameBar?: boolean
  hideInlineError?: boolean
  /** Enable native file picker with action sheet on mobile (Files + Photo Library options) */
  enableNativeFilePicker?: boolean
}

export const FileUpload: FunctionComponent<FileUploadProps> = ({
  label,
  error,
  placeholder,
  subText,
  disabled = false,
  baseInputStyle,
  name,
  isUploading = false,
  onFileSelect,
  acceptedFileTypes,
  maxFileSize,
  selectedFile: controlledSelectedFile,
  onUpload,
  onUploadSuccessful,
  showPreview = true,
  allowClear = true,
  containerHeight = DEFAULT_CONTAINER_HEIGHT,
  onError,
  validateFile,
  showFileNameBar = false,
  hideInlineError = false,
  enableNativeFilePicker = false,
  ...rest
}) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()
  const { showActionSheetWithOptions } = useActionSheet()
  const [hovered, setHovered] = useState(false)
  const [internalSelectedFile, setInternalSelectedFile] = useState<File | null>(null)
  const [uploadingState, setUploadingState] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [errorText, setErrorText] = useState<string | null>(null)

  const imageTypes = useMemo(() => acceptedFileTypes ?? ['image/jpeg', 'image/png', 'image/webp'], [acceptedFileTypes])
  const hasFile = !!(controlledSelectedFile ?? internalSelectedFile)
  const currentlyUploading = isUploading || uploadingState
  const fileForPreview = (controlledSelectedFile ?? internalSelectedFile) || null
  const showFilePreview = hasFile && showPreview && !currentlyUploading
  const showFileSelected = hasFile && !showPreview && !currentlyUploading

  // Convert MIME types array to react-dropzone accept format (e.g., { 'image/jpeg': [], 'image/png': [] })
  const dropzoneAccept = useMemo(() => {
    return imageTypes.reduce((acc, mimeType) => {
      acc[mimeType] = []
      return acc
    }, {} as Record<string, string[]>)
  }, [imageTypes])

  const isImageFile = (file: File): boolean => {
    return file.type.startsWith('image/')
  }

  const defaultValidateFile = useCallback(
    (file: File): FileValidationResult => {
      if (maxFileSize && file.size > maxFileSize) {
        return {
          isValid: false,
          error: {
            code: FileValidationErrorCode.FILE_TOO_LARGE,
          },
        }
      }

      return {
        isValid: true,
      }
    },
    [maxFileSize],
  )

  const handleFileSelection = useCallback(
    async (file: File) => {
      setErrorText(null)

      // Use custom validation if provided, otherwise fallback to default validation
      const validationResult: FileValidationResult = validateFile ? validateFile(file) : defaultValidateFile(file)

      if (!validationResult.isValid) {
        let errorMessage = ''
        const errorCode = validationResult.error?.code

        if (errorCode === FileValidationErrorCode.FILE_TOO_LARGE) {
          errorMessage = formatMessage(FILE_UPLOAD_MESSAGES.fileTooLarge)
        } else if (errorCode === FileValidationErrorCode.UNSUPPORTED_FILE_TYPE) {
          errorMessage = formatMessage(FILE_UPLOAD_MESSAGES.unsupportedFileType)
        } else {
          errorMessage = formatMessage(FILE_UPLOAD_MESSAGES.genericError)
        }

        setErrorText(errorMessage)
        console.error('File validation failed', {
          errorCode,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        })

        // Reset file state
        onFileSelect?.(null)
        setInternalSelectedFile(null)
        onError?.()
        return
      }

      if (onUpload) {
        try {
          setUploadingState(true)
          await onUpload(file)
          onFileSelect?.(file)
          setInternalSelectedFile(file)
          onUploadSuccessful?.(file)
        } catch {
          setErrorText(formatMessage(FILE_UPLOAD_MESSAGES.uploadFailed))
          onFileSelect?.(null)
          setInternalSelectedFile(null)
          onError?.()
        } finally {
          setUploadingState(false)
        }
      } else {
        onFileSelect?.(file)
        setInternalSelectedFile(file)
        onUploadSuccessful?.(file)
      }
    },
    [validateFile, defaultValidateFile, formatMessage, onFileSelect, onError, onUpload, onUploadSuccessful],
  )

  const { getRootProps, getInputProps, isDragActive, inputRef, open } = useWebDropzone({
    accept: dropzoneAccept,
    maxSize: maxFileSize,
    disabled: disabled || currentlyUploading || hasFile,
    onDrop: async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]

      if (file) {
        await handleFileSelection(file)
        // Reset the file input to allow re-uploading the same file
        if (inputRef.current) {
          inputRef.current.value = ''
        }
      }
    },
    onDropRejected: (fileRejections: { errors: Array<{ code: string }> }[]) => {
      const errorCode = fileRejections[0]?.errors[0]?.code
      let message: string
      switch (errorCode) {
        case DropzoneErrorCode.FileTooLarge:
          message = formatMessage(FILE_UPLOAD_MESSAGES.fileTooLarge)
          break
        case DropzoneErrorCode.TooManyFiles:
          message = formatMessage(FILE_UPLOAD_MESSAGES.tooManyFiles)
          break
        case DropzoneErrorCode.FileInvalidType:
        default:
          message = formatMessage(FILE_UPLOAD_MESSAGES.unsupportedFileType)
          break
      }
      setErrorText(message)
      onFileSelect?.(null)
      setInternalSelectedFile(null)
      onError?.()
      // Reset the file input
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    },
  })

  // Image preview generation
  useEffect(() => {
    const file = fileForPreview as FileWithUri | null

    if (!file) {
      setPreviewUrl(null)
      return
    }

    // Skip preview generation for large files
    if (file.size > MAX_PREVIEW_FILE_SIZE) {
      setPreviewUrl(null)
      return
    }

    // On React Native, use the native URI directly for image preview
    if (!IS_WEB && file.uri) {
      setPreviewUrl(file.uri)
      return () => {
        setPreviewUrl(null)
      }
    }

    // On web, use URL.createObjectURL for blob preview
    if (IS_WEB && typeof URL !== 'undefined' && URL.createObjectURL) {
      try {
        const url = URL.createObjectURL(file)
        setPreviewUrl(url)
        return () => {
          if (url && typeof URL !== 'undefined' && URL.revokeObjectURL) {
            URL.revokeObjectURL(url)
          }
        }
      } catch {
        setPreviewUrl(null)
      }
    } else {
      setPreviewUrl(null)
    }

    return
  }, [fileForPreview])

  const pickFromPhotoLibrary = async () => {
    try {
      const file = await pickImageFromLibrary()
      if (file) {
        await handleFileSelection(file)
      }
    } catch (error) {
      console.error('[FileUpload] Failed to pick image from library', { error })
      setErrorText(formatMessage(FILE_UPLOAD_MESSAGES.imageSelectionFailed))
      onError?.()
    }
  }

  const pickFromFiles = async () => {
    try {
      const file = await pickDocumentFromLibrary(acceptedFileTypes)
      if (file) {
        await handleFileSelection(file)
      }
    } catch (error) {
      console.error('[FileUpload] Failed to pick document from files', { error })
      setErrorText(formatMessage(FILE_UPLOAD_MESSAGES.fileSelectionFailed))
      onError?.()
    }
  }

  const handlePress = async () => {
    if (disabled || currentlyUploading) return
    if (IS_WEB && typeof document !== 'undefined') {
      open()
    } else if (enableNativeFilePicker) {
      // Show file picker options
      const chooseFromFilesLabel = formatMessage(FILE_UPLOAD_MESSAGES.chooseFromFiles)
      const chooseFromPhotoLibraryLabel = formatMessage(FILE_UPLOAD_MESSAGES.chooseFromPhotoLibrary)
      const cancelLabel = formatMessage(FILE_UPLOAD_MESSAGES.cancel)

      if (Platform.OS === 'android') {
        // On Android, ActionSheetProvider context is not accessible inside Modal components
        // due to separate native view hierarchy. Using Alert.alert() as a workaround.
        Alert.alert(
          formatMessage(FILE_UPLOAD_MESSAGES.uploadDocument),
          formatMessage(FILE_UPLOAD_MESSAGES.chooseFileSource),
          [
            { text: chooseFromFilesLabel, onPress: () => pickFromFiles() },
            { text: chooseFromPhotoLibraryLabel, onPress: () => pickFromPhotoLibrary() },
            { text: cancelLabel, style: 'cancel' },
          ],
          { cancelable: true },
        )
      } else {
        // iOS uses native UIActionSheet which renders correctly on top of modals
        showActionSheetWithOptions(
          {
            options: [chooseFromFilesLabel, chooseFromPhotoLibraryLabel, cancelLabel],
            cancelButtonIndex: 2,
          },
          async (buttonIndex: number | undefined) => {
            if (buttonIndex === 0) {
              await pickFromFiles()
            } else if (buttonIndex === 1) {
              await pickFromPhotoLibrary()
            }
          },
        )
      }
    } else {
      // Default: Use expo-image-picker to select image from photo library
      await pickFromPhotoLibrary()
    }
  }

  const handleClearFile = () => {
    onFileSelect?.(null)
    setInternalSelectedFile(null)
    setErrorText(null)
    if (previewUrl && IS_WEB && typeof URL !== 'undefined' && URL.revokeObjectURL) {
      URL.revokeObjectURL(previewUrl)
    }
  }

  const displayText =
    (currentlyUploading
      ? formatMessage({ defaultMessage: 'Uploading...', description: 'Uploading state' })
      : placeholder) ||
    formatMessage({
      defaultMessage: 'Upload',
      description: 'Placeholder label for file upload',
    })

  const accessibilityLabel = hasFile
    ? formatMessage({ defaultMessage: 'Selected file. Tap to change file.', description: 'Selected file label' })
    : formatMessage({ defaultMessage: 'Upload file', description: 'Accessibility label for file upload' })

  const renderContent = () => {
    if (currentlyUploading) {
      return (
        <FileUploadContainer
          testID={name ? `${name}-uploading` : 'uploading'}
          hovered={false}
          uploading
          height={containerHeight}
        >
          <LoadingSpinner size={24} />
          <BodyText
            text={formatMessage({
              defaultMessage: 'Uploading...',
              description: 'Text displayed while uploading in progress',
            })}
            size={BodyTextSize.DEFAULT}
            color={colors.textActive}
            textAlign='center'
          />
        </FileUploadContainer>
      )
    }

    if (showFilePreview && fileForPreview) {
      const isImage = isImageFile(fileForPreview)
      return (
        <FilePreviewWrapper>
          <FilePreviewContainer height={containerHeight}>
            {isImage && previewUrl ? (
              <PreviewImageContainer testID={name ? `${name}-image-preview` : 'image-preview'}>
                <Image
                  source={{ uri: previewUrl }}
                  alt={formatMessage({
                    defaultMessage: 'Preview image',
                    description: 'Accessibility label for an image preview',
                  })}
                  contentFit='contain'
                  style={{ width: '100%', height: '100%' }}
                  accessibilityIgnoresInvertColors
                />
              </PreviewImageContainer>
            ) : (
              <UploadContent>
                <UploadIcon size={21} fillColor={colors.iconDefault} />
                <BodyText
                  text={formatMessage({
                    defaultMessage: 'File selected',
                    description: 'Text displayed when a file is selected',
                  })}
                  size={BodyTextSize.DEFAULT}
                  color={colors.textActive}
                  textAlign='center'
                />
              </UploadContent>
            )}
          </FilePreviewContainer>
          {showFileNameBar && (
            <FileNameBar testID={name ? `${name}-file-name-bar` : 'file-name-bar'}>
              <SideNavLibraryIcon size={24} fillColor={colors.iconDefault} />
              <BodyText
                text={fileForPreview.name}
                size={BodyTextSize.DEFAULT}
                color={colors.textSecondary}
                numberOfLines={1}
              />
            </FileNameBar>
          )}
        </FilePreviewWrapper>
      )
    }

    // Show file selected state when showPreview is false but a file is selected
    if (showFileSelected && fileForPreview) {
      return (
        <FileSelectedContainer height={containerHeight}>
          <UploadContent>
            <SideNavLibraryIcon size={32} fillColor={colors.iconDefault} />
            <BodyText
              text={fileForPreview.name}
              size={BodyTextSize.DEFAULT}
              color={colors.textPrimary}
              textAlign='center'
            />
          </UploadContent>
        </FileSelectedContainer>
      )
    }

    return (
      <FileUploadContainer
        hovered={hovered}
        uploading={false}
        height={containerHeight}
        dragging={IS_WEB && isDragActive}
      >
        <UploadContent>
          <View>
            <UploadIcon size={21} fillColor={disabled ? colors.textInactive : colors.iconActive} />
          </View>
          <BodyText text={displayText} size={BodyTextSize.DEFAULT} color={colors.linkDefault} textAlign='center' />
        </UploadContent>
        {subText && (
          <BodyText text={subText} size={BodyTextSize.CAPTION} color={colors.textPrimary} textAlign='center' />
        )}
      </FileUploadContainer>
    )
  }

  const displayError = hideInlineError ? undefined : error || errorText || undefined
  const shouldShowClearButton = allowClear && fileForPreview && (showFilePreview || showFileSelected)

  const dropzoneRootProps = IS_WEB ? getRootProps() : {}
  const dropzoneInputProps = IS_WEB ? getInputProps() : {}

  const renderClearButton = () => {
    if (!shouldShowClearButton) return null

    return (
      <ClearButton
        onPress={handleClearFile}
        testID={name ? `${name}-clear-file` : 'clear-file'}
        accessibilityRole='button'
        accessibilityLabel={formatMessage({
          defaultMessage: 'Remove file',
          description: 'Accessibility label to remove uploaded file',
        })}
        preview={showFilePreview}
      >
        <XIcon size={showFilePreview ? 18 : 12} />
      </ClearButton>
    )
  }

  const renderUploadArea = () => {
    if (showFilePreview || showFileSelected) {
      return (
        <View testID={name ? `${name}-upload-area` : 'file-upload-area'} style={{ position: 'relative' }}>
          {renderContent()}
          {renderClearButton()}
        </View>
      )
    }

    // WEB only: Use react-dropzone for drag-and-drop functionality
    if (IS_WEB && typeof document !== 'undefined') {
      return (
        <DropZoneContainer testID={name ? `${name}-drop-zone` : 'file-drop-zone'}>
          <input
            {...dropzoneInputProps}
            data-testid={name ? `${name}-file-input` : 'file-input'}
            aria-label={accessibilityLabel}
          />
          <div
            {...dropzoneRootProps}
            onClick={handlePress}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handlePress()
              }
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            data-testid={name ? `${name}-upload-area` : 'file-upload-area'}
            role='button'
            tabIndex={disabled || currentlyUploading ? -1 : 0}
            aria-label={accessibilityLabel}
            aria-disabled={disabled || currentlyUploading}
            style={{ cursor: disabled || currentlyUploading ? 'default' : 'pointer' }}
          >
            {renderContent()}
          </div>
        </DropZoneContainer>
      )
    }

    // Native: Use PressableOpacity for tap-to-upload
    return (
      <PressableOpacity
        onPress={handlePress}
        disabled={disabled || currentlyUploading}
        testID={name ? `${name}-upload-area` : 'file-upload-area'}
        accessibilityRole='button'
        accessibilityLabel={accessibilityLabel}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
      >
        {renderContent()}
      </PressableOpacity>
    )
  }

  return (
    <BaseInput name={name} label={label} error={displayError} style={baseInputStyle} {...rest}>
      {renderUploadArea()}
    </BaseInput>
  )
}

export const FileUploadRFF: FunctionComponent<FieldRenderProps<File | null> & FileUploadProps> = ({
  input: { value, onChange, name },
  meta: { touched, error, submitFailed },
  label,
  placeholder,
  disabled,
  baseInputStyle,
  errorOnSubmitFailed = false,
  isUploading = false,
  onUpload,
  onUploadSuccessful,
  acceptedFileTypes,
  maxFileSize,
  showPreview = true,
  allowClear = true,
  containerHeight,
  onError,
  validateFile,
  ...rest
}) => {
  const displayError = errorOnSubmitFailed ? submitFailed && error : touched && error

  const handleFileSelect = (file: File | null) => {
    onChange(file)
  }

  return (
    <FileUpload
      label={label}
      error={displayError}
      placeholder={placeholder}
      disabled={disabled}
      baseInputStyle={baseInputStyle}
      name={name}
      isUploading={isUploading}
      onUpload={onUpload}
      onUploadSuccessful={onUploadSuccessful}
      onFileSelect={handleFileSelect}
      selectedFile={value}
      acceptedFileTypes={acceptedFileTypes}
      maxFileSize={maxFileSize}
      showPreview={showPreview}
      allowClear={allowClear}
      containerHeight={containerHeight}
      onError={onError}
      validateFile={validateFile}
      {...rest}
    />
  )
}
